import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Transaction } from '../../../services/types';
import {
  LionScreen,
  RedCard,
  GreyCard,
  YellowCard,
  SuccessRow,
  InfoRow,
  LionSpinner,
  LION_GREEN,
} from '../components/LionLayout';

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
`;

const CardDesc = styled.div`
  font-size: 12px;
  color: ${LION_GREEN};
  line-height: 1.5;
`;

const ErrorIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 900;
  font-size: 21px;
  margin-right: 24px;
  margin-left: 8px;
  color: rgb(214, 50, 36);
  border: 3px solid rgb(214, 50, 36);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  min-width: 50px;
`;

const ErrorText = styled.div`
  margin: 0 8px 0 0;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  font-size: 18px;
  color: #333;
`;

// ─── Processing ───────────────────────────────────────────────────────────────
export const LionProcessingScreen = ({
  transaction,
  uuid,
  timeLeft,
  onBack,
  receiptAccepted = true,
}: {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onBack: () => void;
  /** CPAYMENT-1616: во время авто-проверки чек ещё не принят — строку не показываем. */
  receiptAccepted?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <LionScreen step={3} uuid={uuid} timeLeft={timeLeft} actions={[{ label: t('common.back'), onClick: onBack, variant: 'grey' }]}>
      <GreyCard>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <LionSpinner size={24} />
          <div>
            <CardTitle>{t('processing.title')}</CardTitle>
            <CardDesc>{t('processing.description')}</CardDesc>
          </div>
        </div>
      </GreyCard>
      {receiptAccepted && (
        <SuccessRow>
          {t('upload.receiptAccepted')} <span>✓</span>
        </SuccessRow>
      )}
    </LionScreen>
  );
};

// ─── Receipt was never accepted (CPAYMENT-1616) ───────────────────────────────
// Тикет уже у оператора, но чек так и не приняли. Экран успеха здесь врёт: клиенту нужно
// понятное действие, а не кнопка «вернуться», поэтому основная кнопка ведёт в поддержку.
export const LionReceiptUnconfirmedScreen = ({
  uuid,
  timeLeft,
  onContactSupport,
}: {
  uuid: string;
  timeLeft: string;
  onContactSupport: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <LionScreen
      step={3}
      uuid={uuid}
      timeLeft={timeLeft}
      actions={[{ label: t('receiptUnconfirmed.action'), onClick: onContactSupport, variant: 'green' }]}
    >
      <YellowCard>
        <CardTitle>{t('receiptUnconfirmed.title')}</CardTitle>
        <CardDesc style={{ color: '#8a6d1f' }}>{t('receiptUnconfirmed.description')}</CardDesc>
      </YellowCard>
    </LionScreen>
  );
};

// ─── Payment window ran out after the client went for the receipt (CPAYMENT-1716) ──
// Клиент нажал «далее», то есть, вероятно, уже платил, но время тикета вышло. Экран отмены
// предлагает лишь вернуться к мерчанту и ничего не говорит про уже отправленный перевод.
export const LionPaymentExpiredScreen = ({
  uuid,
  timeLeft,
  onContactSupport,
}: {
  uuid: string;
  timeLeft: string;
  onContactSupport: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <LionScreen
      step={3}
      uuid={uuid}
      timeLeft={timeLeft}
      actions={[{ label: t('paymentExpired.action'), onClick: onContactSupport, variant: 'green' }]}
    >
      <YellowCard>
        <CardTitle>{t('paymentExpired.title')}</CardTitle>
        <CardDesc style={{ color: '#8a6d1f' }}>{t('paymentExpired.description')}</CardDesc>
      </YellowCard>
    </LionScreen>
  );
};

// ─── Error / Declined ─────────────────────────────────────────────────────────
export const LionErrorScreen = ({
  transaction,
  uuid,
  timeLeft,
  onFinish,
  receiptAccepted,
}: {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onFinish: () => void;
  receiptAccepted?: boolean;
}) => {
  const { t } = useTranslation();
  const method = transaction?.paymentMethod?.includes('SBP') ? 'SBP' : transaction?.paymentMethod;
  return (
    <LionScreen step={3} uuid={uuid} actions={[{ label: t('common.finish'), onClick: onFinish, variant: 'grey', confirmLeave: true }]}>
      <RedCard>
        <ErrorIcon>✕</ErrorIcon>
        <ErrorText>{t('error.paymentError')}</ErrorText>
      </RedCard>
      <InfoRow>{t('common.paymentMethod')} {method}</InfoRow>
      {receiptAccepted && (
        <SuccessRow>{t('upload.receiptAccepted')} <span>✓</span></SuccessRow>
      )}
    </LionScreen>
  );
};

// ─── Expired ──────────────────────────────────────────────────────────────────
export const LionExpiredScreen = ({
  transaction,
  uuid,
  onFinish,
}: {
  transaction: Transaction;
  uuid: string;
  onFinish: () => void;
}) => {
  const { t } = useTranslation();
  const method = transaction?.paymentMethod?.includes('SBP') ? 'SBP' : transaction?.paymentMethod;
  return (
    <LionScreen
      step={3}
      uuid={uuid}
      actions={[
        { label: t('common.finish'), onClick: onFinish, variant: 'grey', confirmLeave: true },
      ]}
    >
      <RedCard>
        <ErrorIcon>✕</ErrorIcon>
        <ErrorText>{t('error.paymentError')}</ErrorText>
      </RedCard>
      <InfoRow style={{ marginTop: 0 }}>{t('error.timeExpired')}</InfoRow>
      <InfoRow>{t('common.paymentMethod')} {method}</InfoRow>
    </LionScreen>
  );
};

// ─── Session expired ──────────────────────────────────────────────────────────
export const LionSessionExpiredScreen = ({
  transaction,
  uuid,
  onFinish,
}: {
  transaction: Transaction;
  uuid: string;
  onFinish: () => void;
}) => {
  const { t } = useTranslation();
  const method = transaction?.paymentMethod?.includes('SBP') ? 'SBP' : transaction?.paymentMethod;
  return (
    <LionScreen step={3} uuid={uuid} actions={[{ label: t('common.finish'), onClick: onFinish, variant: 'grey', confirmLeave: true }]}>
      <RedCard>
        <ErrorIcon>✕</ErrorIcon>
        <ErrorText>{t('error.sessionExpired')}</ErrorText>
      </RedCard>
      <InfoRow>{t('common.paymentMethod')} {method}</InfoRow>
    </LionScreen>
  );
};

// ─── Server error ─────────────────────────────────────────────────────────────
export const LionServerErrorScreen = ({
  transaction,
  uuid,
  timeLeft,
  onReturn,
}: {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onReturn: () => void;
}) => {
  const { t } = useTranslation();
  const method = transaction?.paymentMethod?.includes('SBP') ? 'SBP' : transaction?.paymentMethod;
  return (
    <LionScreen step={3} uuid={uuid} timeLeft={timeLeft} actions={[{ label: t('common.returnToMerchant'), onClick: onReturn, variant: 'grey', confirmLeave: true }]}>
      <RedCard>
        <ErrorIcon>✕</ErrorIcon>
        <ErrorText>{t('error.serverError')}</ErrorText>
      </RedCard>
      <InfoRow>{t('common.paymentMethod')} {method}</InfoRow>
      <GreyCard>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LionSpinner size={24} />
          <span style={{ fontSize: 14, color: '#555' }}>{t('processing.pleaseWait')}</span>
        </div>
      </GreyCard>
    </LionScreen>
  );
};
